import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../firebase-config'
import Seo from '../components/Seo'
import { site, breadcrumbSchema } from '../seo'

function Exercises() {
  const [exercises, setExercises] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState({
    difficulty: 'all',
    category: 'all'
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch exercises
        const exercisesSnapshot = await getDocs(collection(db, 'exercises'))
        const exercisesList = exercisesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        setExercises(exercisesList.sort((a, b) => (a.title || '').localeCompare(b.title || '')))

        // Fetch categories
        const categoriesSnapshot = await getDocs(collection(db, 'categories'))
        const categoriesList = categoriesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        setCategories(categoriesList.sort((a, b) => a.name.localeCompare(b.name)))

        setLoading(false)
      } catch (error) {
        console.error('Error fetching data:', error)
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const filteredExercises = exercises.filter(exercise => {
    const term = search.trim().toLowerCase()
    const matchesSearch = !term ||
      (exercise.title || '').toLowerCase().includes(term) ||
      (exercise.description || '').toLowerCase().includes(term)

    return matchesSearch &&
      (filter.difficulty === 'all' || exercise.difficulty === filter.difficulty) &&
      (filter.category === 'all' || exercise.category === filter.category)
  })

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilter(prev => ({
      ...prev,
      [name]: value
    }))
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 flex justify-center">
        <div className="text-xl">Loading exercises...</div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-16 pt-32">
      <Seo
        title={site.routes['/exercises'].title}
        description={site.routes['/exercises'].description}
        schema={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Exercise Library', path: '/exercises' }
        ])}
      />
      <h1 className="text-4xl font-bold mb-2 text-center">Exercise Library</h1>
      <p className="text-gray-600 text-center mb-8">
        Browse individual exercises, or{' '}
        <Link to="/workouts" className="text-indigo-600 hover:text-indigo-800 font-medium">
          follow a full workout
        </Link>.
      </p>

      {exercises.length === 0 ? (
        <div className="text-center text-gray-500">
          <p>No exercises available yet. Check back soon!</p>
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="bg-gray-100 p-4 rounded-lg mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search
                </label>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="e.g. squat"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
                />
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Difficulty
                </label>
                <select
                  name="difficulty"
                  value={filter.difficulty}
                  onChange={handleFilterChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
                >
                  <option value="all">All Difficulties</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  name="category"
                  value={filter.category}
                  onChange={handleFilterChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
                >
                  <option value="all">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Exercises Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredExercises.length === 0 ? (
              <div className="col-span-full text-center text-gray-500 py-8">
                No exercises match your selected filters.
              </div>
            ) : (
              filteredExercises.map(exercise => (
                <Link
                  to={`/exercises/${exercise.id}`}
                  key={exercise.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
                >
                  {exercise.videoUrl && (
                    <div className="aspect-w-16 aspect-h-9">
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <div className="text-gray-500 flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="p-6">
                    <h2 className="text-xl font-bold mb-2">{exercise.title}</h2>
                    <p className="text-gray-600 mb-4 line-clamp-2">{exercise.description}</p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                        {exercise.difficulty}
                      </span>
                      {exercise.duration && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                          {exercise.duration} min
                        </span>
                      )}
                      <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
                        {exercise.category}
                      </span>
                    </div>

                    <div className="text-indigo-600 font-medium">
                      View exercise &rarr;
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>

          {/* Call to Action Banner */}
          <div className="bg-indigo-100 rounded-lg p-6 mt-12">
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-2xl font-bold text-indigo-800 mb-3">
                Need a personalised workout plan?
              </h3>
              <p className="text-indigo-700 mb-6 max-w-2xl mx-auto">
                Alex can create a customised workout plan tailored to your specific needs, goals, and fitness level.
              </p>
              <Link
                to="/book-consultation"
                className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-8 rounded-lg transition duration-200"
              >
                Book a Consultation
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default Exercises
